/* =========================================================================
   InlineNudge — the reusable, calm "earned moment" upgrade prompt.

   Borrows the hero assurance-ledger container recipe so it reads as the
   same archival family the user already trusts: a faint heartwood tint,
   an ≤8% heartwood hairline, and a single faint brass top-edge inset
   instead of a drop shadow. Never an alert box. Brass is rationed to two
   marks at most (the lead bead + the outline pill / proof underline);
   the CTA pill is brass-outline that fills heartwood on hover — never
   spark (an upsell is not the page's primary action).
   ========================================================================= */

.inudge {
  position: relative;
  background: rgba(27, 58, 46, 0.04); /* ≤4% heartwood tint */
  border: 1px solid rgba(27, 58, 46, 0.08); /* ≤8% heartwood hairline */
  border-radius: 12px;
  box-shadow: inset 0 1px 0 rgba(181, 133, 60, 0.2); /* faint brass seal line */
  padding: 20px 24px;
  /* Calm entrance — fires after the value moment, fades up from below so
     it never displaces anything the user is already reading. */
  animation: inudgeIn 360ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes inudgeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Leaving state — quiet fade up-and-out before unmount, so no empty gap
   lingers. The component unmounts after the transition completes. */
.inudge--leaving {
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 220ms ease, transform 220ms ease;
}

/* Eyebrow: a brass bead (the climb's "landing reached" language) + a
   tracked caption label in heartwood. */
.inudge__eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.inudge__bead {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--brass);
  flex: none;
}
.inudge__eyebrow-label {
  font-family: var(--f-sans);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--heartwood);
}

.inudge__headline {
  font-family: var(--f-serif);
  font-size: 19px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--ink);
  margin: 0 0 6px;
}

.inudge__support {
  font-family: var(--f-sans);
  font-size: 14.5px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--ink);
  max-width: 440px;
  margin: 0;
}

/* Brass proof / "learn more" link — marks the path to fuller detail. */
.inudge__proof {
  display: inline-block;
  margin-top: 10px;
  font-family: var(--f-sans);
  font-size: 13.5px;
  font-weight: 500;
  color: var(--brass-deep);
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: color-mix(in srgb, var(--brass) 55%, transparent);
  transition: text-decoration-color 140ms ease, color 140ms ease;
}
.inudge__proof:hover {
  color: var(--brass);
  text-decoration-color: var(--brass);
}

/* Optional manifest block (Education locked-lesson "here's what's
   included"): a mono scope line between hairlines + a checked list. */
.inudge__manifest {
  margin-top: 16px;
}
.inudge__scope {
  font-family: var(--f-mono);
  font-size: 12px;
  letter-spacing: 0.06em;
  color: var(--brass-deep);
  padding: 12px 0;
  border-top: 1px solid rgba(27, 58, 46, 0.1);
  border-bottom: 1px solid rgba(27, 58, 46, 0.1);
  margin-bottom: 14px;
}
.inudge__includes {
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.inudge__inc {
  display: flex;
  align-items: center;
  gap: 9px;
  font-family: var(--f-sans);
  font-size: 14px;
  color: var(--ink);
}
.inudge__inc svg {
  width: 16px;
  height: 16px;
  flex: none;
  stroke: var(--heartwood);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* Action pill: brass-outline → fills heartwood on hover. The established
   deliverable-card CTA pattern — a calm optional next step, not "ACT NOW". */
.inudge__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  margin-top: 16px;
  border: 1px solid var(--brass);
  border-radius: 999px;
  background: transparent;
  font-family: var(--f-sans);
  font-size: 14px;
  font-weight: 500;
  color: var(--brass-deep);
  text-decoration: none;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}
.inudge__pill:hover {
  background: var(--heartwood);
  border-color: var(--heartwood);
  color: var(--parchment);
}

/* Quiet secondary link beneath the pill (e.g. "Read the first lesson free"). */
.inudge__footerlink {
  display: block;
  margin-top: 14px;
  font-family: var(--f-sans);
  font-size: 13.5px;
  font-weight: 500;
  color: var(--brass-deep);
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: color-mix(in srgb, var(--brass) 55%, transparent);
  transition: text-decoration-color 140ms ease, color 140ms ease;
}
.inudge__footerlink:hover {
  color: var(--brass);
  text-decoration-color: var(--brass);
}

/* Dismiss affordance — a quiet × top-right, ≥28px tap target. */
.inudge__x {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(15, 23, 20, 0.55);
  cursor: pointer;
  transition: color 140ms ease, background-color 140ms ease;
}
.inudge__x:hover {
  color: var(--ink);
  background: rgba(27, 58, 46, 0.06);
}
.inudge__x svg {
  width: 16px;
  height: 16px;
}

@media (max-width: 520px) {
  .inudge {
    padding: 16px 20px;
  }
}

/* Honor reduced-motion: cross-fade only, no vertical travel. */
@media (prefers-reduced-motion: reduce) {
  .inudge {
    animation: inudgeFade 240ms ease both;
  }
  .inudge--leaving {
    transform: none;
  }
  @keyframes inudgeFade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
